import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { ETemplateCategory } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplate } from '../TemplateEditor/types';

export interface IETTTemplateManagerProps {
    templates: ITemplate[];
    onCreateTemplate?: (template: Omit<ITemplate, 'id'>) => Promise<unknown>;
    onUpdateTemplate?: (template: ITemplate) => void;
    onDeleteTemplate?: (id: number) => void;
    onEditTemplate?: (id: number) => void;
}

export const ETTTemplateManager: React.FC<IETTTemplateManagerProps> = ({
    templates,
    onCreateTemplate,
    onUpdateTemplate,
    onDeleteTemplate,
    onEditTemplate,
}) => {
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;
    const oftenUsed = langStrings.oftenUsed;

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newTemplate, setNewTemplate] = useState<Omit<ITemplate, 'id'>>({
        title: '',
        description: '',
        templateVersion: '1.0', // Geändert von version zu templateVersion
        templateCategory: ETemplateCategory.CUSTOM,
        pages: [],
        isActive: true,
    });

    const handleCreateTemplate = () => {
        if (onCreateTemplate) {
            onCreateTemplate(newTemplate)
                .then(() => {
                    setIsAddDialogOpen(false);
                    setNewTemplate({
                        title: '',
                        description: '',
                        templateVersion: '1.0', // Geändert von version zu templateVersion
                        templateCategory: ETemplateCategory.CUSTOM,
                        pages: [],
                        isActive: true,
                    });
                })
                .catch(showErrorDialog);
        }
    };

    const openAddDialog = () => {
        setIsAddDialogOpen(true);
    };

    const renderAddDialog = () => {
        if (!isAddDialogOpen) return null;

        return (
            <Dialog
                id="create-template-dialog"
                title={langStrings.createTemplate}
                onClose={() => setIsAddDialogOpen(false)}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button text={oftenUsed.cancel} onClick={() => setIsAddDialogOpen(false)} />
                        <Button
                            text={oftenUsed.create}
                            color="primary"
                            disabled={!newTemplate.title.trim()}
                            onClick={handleCreateTemplate}
                        />
                    </div>
                }
            >
                <TextField
                    fullWidth
                    label={langStrings.name}
                    value={newTemplate.title}
                    onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                    margin="normal"
                    required
                />
                <TextField
                    fullWidth
                    label={oftenUsed.description}
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    margin="normal"
                    multiline
                    rows={3}
                />
                <TextField
                    fullWidth
                    label={oftenUsed.version}
                    value="1.0"
                    disabled
                    margin="normal"
                    helperText={langStrings.versionReadOnly}
                />
            </Dialog>
        );
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">{langStrings.templateManager}</Typography>
                {onCreateTemplate && (
                    <Button
                        text={langStrings.createTemplate}
                        icon={<AddIcon />}
                        color="primary"
                        onClick={openAddDialog}
                    />
                )}
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{langStrings.name}</TableCell>
                            <TableCell>{oftenUsed.description}</TableCell>
                            <TableCell>{oftenUsed.version}</TableCell>
                            <TableCell>{langStrings.status}</TableCell>
                            <TableCell align="right">{oftenUsed.actions}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.map((template) => (
                            <TableRow key={template.id}>
                                <TableCell>{template.title}</TableCell>
                                <TableCell>{template.description}</TableCell>
                                <TableCell>{template.templateVersion}</TableCell>{' '}
                                {/* Geändert von version zu templateVersion */}
                                <TableCell>{template.isActive ? langStrings.active : langStrings.inactive}</TableCell>
                                <TableCell align="right">
                                    {onEditTemplate && (
                                        <IconButton onClick={() => onEditTemplate(template.id)}>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                    {onDeleteTemplate && (
                                        <IconButton onClick={() => onDeleteTemplate(template.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {templates.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {langStrings.noTemplatesFound}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {renderAddDialog()}
        </Box>
    );
};

export default ETTTemplateManager;
